/* La orden de gas oil: qué dice el correo y a quién va. PURO.
 *
 * Ticket 431 (Lautaro Díaz, Satelital, 16/10/2026): cuando un chofer en ruta
 * no tiene la tarjeta YPF, se le rompió o no tiene cupo, Satelital le pasa por
 * correo una orden a una YPF con cuenta corriente. «Así como se genera el
 * correo de los enganches»: lo manda el que la genera, desde su correo, con
 * «Abrir en mi correo» y «Copiar el correo». De acá no sale ningún mail.
 *
 * ── DE DÓNDE SALE CADA COSA ─────────────────────────────────────────────────
 *
 *   · el formato, del correo de ejemplo que adjuntó: «Buen dia, favor de
 *     abastecer con 300lts de gas oil comun al chofer que detallo:», la
 *     tabla «ORDEN DE GAS OIL - MARTINEZ JOSE» (INT, EMP, MARCA MOD, AÑO,
 *     DOMINIO, APELLIDOS Y NOMBRE DE CHOFERES, DNI, TELEFONOS), el motivo y
 *     «Debe ser facturada a … cuit: …» resaltado;
 *   · las direcciones, de su planilla «ORDEN DE GAS OIL.xlsx»: el Para de
 *     cada YPF y la misma copia para las dos;
 *   · el saludo, según la hora argentina: «Buen día» antes de las 12 y
 *     «Buenas tardes» desde las 12 (lo pidió para todos sus correos).
 */

import { cuitLindo, siglaDe } from "./empresa";
import { diaAr, diaHoraAr, enAR, horaAr, hoyAr } from "./fechaAr";

export const YPFS = [
  {
    id: "mica",
    nombre: "YPF MICA TRELEW",
    para: ["micatrelew@yahoo.com.ar", "playa.pesados@ypfmica.com", "cristina.cardozo@ypfmica.com"],
  },
  {
    id: "caleta",
    nombre: "YPF CALETA OLIVIA OTAMENDI",
    para: ["macosta@otamendiycia.com", "playasc@otamendiycia.com"],
  },
];

/* La copia de su planilla: «JORGE CESTONA, DAIANA CANTARINI, TODOS LOS
 * CORREOS DE SATELITAL, MARCOS CARO, NICOLAS CANTARINI, NICOLAS CRAGNO, LA
 * VIGILANCIA, BRAIAN PIRELLI». Satelital son los mismos cinco del correo de
 * los enganches (`enganches_destinos.SATELITAL` en FlotaBot). */
export const COPIA = [
  "jorge@cantarini.com.ar",
  "dcantarini@cantarini.com.ar",
  "ldiaz@cantarini.com.ar",
  "lastroza@cantarini.com.ar",
  "glynch@cantarini.com.ar",
  "satelital.cantarini@gmail.com",
  "satelital2cantarini@gmail.com",
  "mcaro@cantarini.com.ar",
  "nico@cantarini.com.ar",
  "ncragno@cantarini.com.ar",
  "vigilancia@cantarini.com.ar",
  "bpirelli@cantarini.com.ar",
];

/* La copia sin el que manda (ticket 467, Lautaro, 20/10: «Que el que envie el
 * correo no este en copia»): el correo sale de su casilla, no hace falta que
 * le llegue otra vez. `mios` son las direcciones de quien está en el hub —la
 * de la sesión y la de su ficha en `users`—, sin distinguir mayúsculas. La
 * copia de siempre (`COPIA`) no se toca: se filtra al mostrarla. */
export function menosElQueManda(copia, mios) {
  const norma = (d) => String(d ?? "").trim().toLowerCase();
  const fuera = new Set([].concat(mios ?? []).map(norma).filter(Boolean));
  return (copia || []).filter((d) => !fuera.has(norma(d)));
}

/* El motivo como lo elige (sus tres) y la frase que va en el correo. La de
 * «sin cupo» es la de su ejemplo, tal cual. */
export const MOTIVOS = [
  { id: "no_posee", nombre: "NO POSEE LA TARJETA YPF", frase: "El chofer no posee la tarjeta ypf en ruta" },
  { id: "sin_cupo", nombre: "SIN CUPO EN LA TARJETA", frase: "La tarjeta ypf en ruta no tiene cupo" },
  { id: "rotura", nombre: "ROTURA DE LA TARJETA", frase: "La tarjeta ypf en ruta está rota" },
];

/* A quién se factura: las empresas del grupo, con el nombre como va en el
 * correo y su CUIT (`shared/empresa`). */
export const EMPRESAS_FACTURA = [
  { sigla: "TC", nombre: "Transportes Cantarini", cuit: "30610890403" },
  { sigla: "EX", nombre: "Expreso Cantarini", cuit: "30644511304" },
  { sigla: "TA", nombre: "Transamerica Transportes S.A.", cuit: "30683612916" },
  { sigla: "TCC", nombre: "Transcan Cargas s.r.l", cuit: "33719349949" },
  { sigla: "JK", nombre: "Jow Kon", cuit: "33714052689" },
];

export const CELDAS = ["INT", "EMP", "MARCA MOD", "AÑO", "DOMINIO", "APELLIDOS Y NOMBRE DE  CHOFERES", "DNI", "TELEFONOS"];

const renglon = (v) => String(v ?? "").replace(/\s+/g, " ").trim();

export function saludo(ahora = new Date()) {
  const d = enAR(ahora);
  return d && d.getUTCHours() < 12 ? "Buen día," : "Buenas tardes,";
}

/* «MARTINEZ» + «JOSE OSCAR» → «MARTINEZ JOSE», como lo escribe él. */
export function nombreCorto(apellido, nombres) {
  const a = renglon(apellido).toUpperCase();
  const n = renglon(nombres).toUpperCase().split(" ")[0] || "";
  return [a, n].filter(Boolean).join(" ");
}

/* El asunto lleva el interno del camión (ticket 467, Lautaro, 20/10: «en el
 * titulo agregale el interno del camion asi:», con el correo que mandó Leonel
 * ese día): «ORDEN DE GAS OIL INT 1016 - FANLOO EXEQUIEL», con espacios
 * simples. Sin un interno de verdad —sin tractor asignado, FLT de un fletero,
 * una patente, un guion— va como antes, sin «INT». La tabla del cuerpo sigue
 * con su título de siempre, «ORDEN DE GAS OIL - FANLOO EXEQUIEL». */
export function asuntoDe(chofer) {
  const i = renglon(chofer?.interno);
  const interno = /^\d{1,5}$/.test(i) && Number(i) > 0 ? ` INT ${Number(i)}` : "";
  return `ORDEN DE GAS OIL${interno} - ${nombreCorto(chofer?.apellido, chofer?.nombres)}`;
}

export function dniLindo(v) {
  const d = String(v ?? "").replace(/\D/g, "").replace(/^0+/, "");
  return d ? d.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : "";
}

/* «AI242EW» → «AI 242 EW»; «AB123CD» igual; «KOM245» → «KOM 245». */
export function dominioLindo(v) {
  const p = String(v ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  let m = p.match(/^([A-Z]{2})(\d{3})([A-Z]{2})$/);
  if (m) return `${m[1]} ${m[2]} ${m[3]}`;
  m = p.match(/^([A-Z]{3})(\d{3})$/);
  if (m) return `${m[1]} ${m[2]}`;
  return p;
}

/* «11 3683 8087» → «11-3683-8087». */
export function telefonoLindo(v) {
  return renglon(v).replace(/\s*-\s*/g, "-").replace(/ /g, "-");
}

export function litrosValidos(v) {
  const n = Number(String(v ?? "").trim());
  return Number.isInteger(n) && n > 0 && n <= 2000 ? n : null;
}

/* «Tanque completo» (ticket 449, Lautaro, 19/10: «hay veces en las que los
 * choferes completan los tanques»). En el correo va donde iban los litros:
 * «favor de abastecer con TANQUE COMPLETO de gas oil comun…». En la base es
 * `tanque_completo` en true con `litros` null, y el CHECK de la migración
 * 20261021071100 ata una cosa a la otra: una orden sin litros es de tanque
 * completo y nada más. Por eso el historial lo reconoce por los litros
 * vacíos sin pedir la columna nueva, y lee igual con la migración aplicada o
 * sin aplicar. */
export const TANQUE_COMPLETO = "TANQUE COMPLETO";

export function litrosDelHistorial(f) {
  return f?.tanque_completo === true || f?.litros == null ? "Tanque completo" : f.litros;
}

/* El cambio de litros autorizado (Lautaro y Nico, 21/10: «en el historial
 * queda los lts originales que yo pase» / «que puedan cambiarlo con mi
 * autorización»). Una orden cambiada muestra los litros que de verdad cargó el
 * chofer y, abajo, lo que se había pedido y quién lo autorizó: «Pedidos: 300 l
 * · autorizó Nicolás Cantarini», o «Pedido: tanque completo · …». La migración
 * 20261023172118 guarda lo pedido la primera vez en `litros_pedidos`, y lo deja
 * null cuando se había pedido tanque completo: lo que dice que se cambió es
 * `litros_editados_en`. Sin cambio (o sin la migración), "". */
export function litrosPedidos(f) {
  if (!f?.litros_editados_en) return "";
  const p = litrosValidos(f.litros_pedidos);
  const pedido = p ? `Pedidos: ${p} l` : "Pedido: tanque completo";
  const quien = renglon(f.litros_autorizados_por);
  return quien ? `${pedido} · autorizó ${quien}` : pedido;
}

/* Lo que se precarga al abrir «Pedir cambio de litros»: los litros de la
 * orden, o nada si era de tanque completo. */
export function litrosParaEditar(f) {
  return f?.tanque_completo === true || f?.litros == null ? "" : String(f.litros);
}

/* ¿Tiene un pedido de cambio esperando a Nico? Mientras tanto no se pide otro
 * (la base deja uno pendiente por orden). */
export function cambioPendiente(f) {
  return f?.cambio?.estado === "pendiente" ? f.cambio : null;
}

/* Lo que dice la celda del pedido de cambio: el que espera y, si el último se
 * rechazó, eso. Uno autorizado ya está en los litros; uno retirado no se
 * muestra. */
export function estadoCambio(f) {
  const c = f?.cambio;
  const lts = litrosValidos(c?.litros_nuevos);
  if (!c || !lts) return "";
  if (c.estado === "pendiente") return `Pendiente de autorización: ${lts} l`;
  if (c.estado === "rechazado") return `Rechazado: ${lts} l`;
  return "";
}

/* Las celdas de la fila del chofer, en el orden de su tabla. */
export function filaDelChofer(ch) {
  return [
    renglon(ch?.interno),
    ch?.empresa_tractor ? siglaDe(ch.empresa_tractor) || "" : "",
    renglon(ch?.marca).toUpperCase(),
    renglon(ch?.anio),
    dominioLindo(ch?.dominio),
    nombreCorto(ch?.apellido, ch?.nombres),
    dniLindo(ch?.dni),
    telefonoLindo(ch?.telefono),
  ];
}

/* La empresa del tractor, para arrancar la elección con ella. */
export function empresaDelTractor(ch) {
  const d = String(ch?.empresa_tractor ?? "").replace(/\D/g, "");
  return EMPRESAS_FACTURA.find((e) => e.cuit === d) || null;
}

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const TD = "border:1px solid #000;padding:4px 8px;text-align:center;vertical-align:middle;font-size:12px";
const TD_TITULO =
  "border:1px solid #000;padding:6px;text-align:center;font-size:22px;font-weight:bold;font-style:italic;text-decoration:underline";
const TD_ENCABEZADO = TD + ";background:#ff0000;color:#ffffff;font-weight:bold";
const TD_DATO = TD + ";font-weight:bold;font-style:italic";
const FACTURA = "background-color:#ffff00;font-weight:bold;font-style:italic";

/* El correo entero. `orden`: { chofer, ypf, empresa, litros, tanqueCompleto,
 * motivo, firma }; con `tanqueCompleto` los litros no se miran.
 * Devuelve { asunto, para, copia, html, texto, titulo, faltan }. */
export function armar({ chofer, ypf, empresa, litros, tanqueCompleto = false, motivo, firma = "", ahora = new Date() }) {
  const nombre = nombreCorto(chofer?.apellido, chofer?.nombres);
  const titulo = `ORDEN DE GAS OIL - ${nombre}`;
  const hola = saludo(ahora);
  const cuanto = tanqueCompleto ? TANQUE_COMPLETO : `${litros}lts`;
  const pedido = `favor de abastecer con ${cuanto} de gas oil comun al chofer que detallo:`;
  const fila = filaDelChofer(chofer);
  const frase = motivo?.frase || "";
  const factura = `Debe ser facturada a ${empresa?.nombre || ""} cuit: ${cuitLindo(empresa?.cuit)}`;
  // Un mail no es una firma: sin nombre en el hub, va «Saludos atte.» solo.
  const firmaLimpia = renglon(firma).includes("@") ? "" : renglon(firma);

  const faltan = CELDAS.filter((_, i) => !fila[i]);

  const html =
    '<div style="font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#000">' +
    `<p>${esc(hola)}<br>${esc(pedido)}</p>` +
    '<table cellspacing="0" cellpadding="4" style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif">' +
    `<tr><td colspan="${CELDAS.length}" style="${TD_TITULO}">${esc(titulo)}</td></tr>` +
    `<tr>${CELDAS.map((h) => `<td style="${TD_ENCABEZADO}">${esc(h)}</td>`).join("")}</tr>` +
    `<tr>${fila.map((v) => `<td style="${TD_DATO}">${esc(v)}</td>`).join("")}</tr>` +
    "</table>" +
    `<p>${esc(frase)}<br><span style="${FACTURA}">${esc(factura)}</span><br>Muchas gracias.</p>` +
    `<p>Saludos atte.${firmaLimpia ? `<br>${esc(firmaLimpia)}` : ""}</p>` +
    "</div>";

  const texto = [
    `${hola}\n${pedido}`,
    `${titulo}\n${CELDAS.map((h, i) => `${h.replace(/\s+/g, " ")}: ${fila[i]}`).join(" | ")}`,
    `${frase}\n${factura}\nMuchas gracias.`,
    `Saludos atte.${firmaLimpia ? `\n${firmaLimpia}` : ""}`,
  ].join("\n\n");

  return {
    asunto: asuntoDe(chofer),
    titulo,
    para: [...(ypf?.para || [])],
    copia: [...COPIA],
    html,
    texto,
    faltan,
  };
}

const UNA = /^[^@\s,;<>"']+@[^@\s,;<>"']+\.[A-Za-z]{2,}$/;
const lista = (ds) =>
  (ds || [])
    .filter((d) => UNA.test(String(d)))
    .map((d) => encodeURIComponent(d).replace(/%40/g, "@"))
    .join(",");

/* «Abrir en mi correo»: el Para, la copia y el asunto. Sin el cuerpo: un
 * `mailto:` lleva sólo texto plano y la orden lleva una tabla. Es el mismo
 * armado que `enganches_correo.mailto` en FlotaBot. */
export function mailto(c) {
  const campos = [];
  const cc = lista(c?.copia);
  if (cc) campos.push(`cc=${cc}`);
  if (c?.asunto) campos.push(`subject=${encodeURIComponent(c.asunto)}`);
  return `mailto:${lista(c?.para)}${campos.length ? `?${campos.join("&")}` : ""}`;
}

/* Cuál orden es, para el «Borrar» del historial (ticket 448, Lautaro, 19/10):
 * «FANLOO EXEQUIEL, 300 l, del 19/10 a las 09:35». Chofer, litros, día y hora
 * argentinos. La HORA hace falta: el 19/10 había dos órdenes de FANLOO por
 * 300 l, una de las 09:35 y otra de las 15:44, y sin hora la pregunta era la
 * misma para las dos. Además la pregunta va en un renglón que ensancha la
 * tabla, y en el teléfono deja la celda «Cuándo» fuera de vista justo al
 * confirmar. */
function cuandoOrden(f) {
  const dia = diaAr(f?.creado_en).slice(0, 5);
  const hora = dia ? horaAr(f?.creado_en) : "";
  return dia ? `del ${dia}${hora ? ` a las ${hora}` : ""}` : "";
}

function cualOrden(f) {
  const lts = litrosValidos(f?.litros);
  return [renglon(f?.chofer), lts ? `${lts} l` : "", cuandoOrden(f)].filter(Boolean).join(", ");
}

/* «¿Borrar la orden de FANLOO EXEQUIEL, 300 l, del 19/10 a las 09:35?» */
export function preguntaBorrar(f) {
  const cual = cualOrden(f);
  return cual ? `¿Borrar la orden de ${cual}?` : "¿Borrar la orden?";
}

/* «Se borró la orden de FANLOO EXEQUIEL, 300 l, del 19/10 a las 09:35.» */
export function avisoBorrada(f) {
  const cual = cualOrden(f);
  return cual ? `Se borró la orden de ${cual}.` : "Se borró la orden.";
}

/* «Se pidió pasar a 450 l la orden de FANLOO EXEQUIEL del 19/10 a las 09:35.» */
export function avisoCambioPedido(f, litros) {
  const lts = litrosValidos(litros);
  const de = [renglon(f?.chofer), cuandoOrden(f)].filter(Boolean).join(" ");
  if (!lts) return "Se pidió el cambio.";
  return de ? `Se pidió pasar a ${lts} l la orden de ${de}.` : `Se pidió pasar a ${lts} l la orden.`;
}

/* ── La foto del ticket que mandó el chofer (Nico, 28/10, ticket 545) ────────
 *
 * «Cuando ellos suben la foto, ¿no puede aparecer ahí junto a la orden? Por
 * la fecha, el chofer y el interno». La foto y lo leído de ella están en
 * `tickets_combustible` (FlotaBot, ticket 525). Ninguna de las dos tablas
 * apunta a la otra, así que el cruce es por los datos:
 *
 *   · el mismo INTERNO (sólo los dígitos: «INT 1003» = «1003») o el mismo
 *     DNI (sólo dígitos, sin ceros adelante) contra el del padrón
 *     (`dni_chofer`) o el que dice el ticket (`dni_ticket`);
 *   · la carga entre `ANTES_H` horas antes de la orden y `DESPUES_H` después:
 *     la orden sale y el chofer llega a la YPF en el día o al siguiente; para
 *     atrás, un margen por si la orden se cargó en el hub después de pasarla
 *     por teléfono. Igna, 28/10: 36 h y no 72 — con tres días entraban
 *     cargas de tarjeta del mismo camión que no eran de la orden.
 *
 * La hora de la carga es la del ticket (`fecha_hora`, con lo que corrigió
 * Tráfico encima); si no se leyó, cuándo llegó la foto. Si hay varias, va
 * SÓLO la más cercana a la orden (Igna, 28/10): una orden es una carga. */

export const ANTES_H = 6;
export const DESPUES_H = 36;
// Más de esto de diferencia con los litros de la orden se marca.
export const TOLERANCIA_LITROS = 0.05;

const HORA = 3600 * 1000;
const digitos = (v) => String(v ?? "").replace(/\D/g, "");
const dniNorm = (v) => digitos(v).replace(/^0+/, "");

// Lo que Tráfico corrigió va encima de lo leído (como `con_correccion` del bot).
const CORREGIBLES = ["fecha_hora", "establecimiento", "localidad", "litros", "producto", "kilometraje"];
export function ticketCorregido(t) {
  const c = t?.correccion && typeof t.correccion === "object" ? t.correccion : {};
  const encima = Object.fromEntries(Object.entries(c).filter(([k]) => CORREGIBLES.includes(k)));
  return { ...t, ...encima };
}

/* El instante de la carga: la del ticket o, si no se leyó, cuándo llegó. */
export function cuandoCarga(t) {
  for (const v of [t?.fecha_hora, t?.recibida_en, t?.creado_en]) {
    const ms = v ? Date.parse(v) : NaN;
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

function esDelChofer(orden, t) {
  const int = digitos(orden?.interno);
  if (int && int === digitos(t?.interno)) return true;
  const dni = dniNorm(orden?.dni);
  return Boolean(dni) && (dni === dniNorm(t?.dni_chofer) || dni === dniNorm(t?.dni_ticket));
}

/* El ticket de esa orden: el más cercano, en una lista de uno. [] si no hay. */
export function fotosDeLaOrden(orden, tickets) {
  const base = Date.parse(orden?.creado_en);
  if (!Number.isFinite(base)) return [];
  return (tickets || [])
    .map(ticketCorregido)
    .map((t) => ({ t, ms: cuandoCarga(t) }))
    .filter(({ t, ms }) => ms !== null && ms >= base - ANTES_H * HORA && ms <= base + DESPUES_H * HORA && esDelChofer(orden, t))
    .sort((a, b) => Math.abs(a.ms - base) - Math.abs(b.ms - base))
    .slice(0, 1)
    .map(({ t }) => t);
}

/* Lo que dice la celda cuando no hay foto (ticket 561, Nico, 29/10: «Está la
 * opción pero no hay ninguna foto»). No era que no se subieran: de las 50
 * órdenes que mostraba el historial, NINGUNA tenía un ticket del chofer en
 * su ventana —los choferes mandan pocas fotos a FlotaBot—, y «sin foto del
 * chofer todavía» en todos los renglones parecía una función rota. Ahora se
 * distingue la orden a la que todavía le puede llegar (dentro de las
 * `DESPUES_H` horas) de la que ya pasó su ventana sin foto. */
export function sinFotoDelChofer(orden, ahora = Date.now()) {
  const base = Date.parse(orden?.creado_en);
  const ms = ahora instanceof Date ? ahora.getTime() : ahora;
  if (Number.isFinite(base) && ms <= base + DESPUES_H * HORA) return "la foto del chofer todavía no llegó";
  return "no llegó foto del chofer";
}

/* Desde cuándo hay que traer tickets para cubrir esas órdenes (ISO), o "".
 * La fila del ticket se escribe después de la carga, así que alcanza con
 * filtrar por su `creado_en`. */
export function desdeParaTickets(ordenes) {
  const ms = (ordenes || []).map((o) => Date.parse(o?.creado_en)).filter(Number.isFinite);
  return ms.length ? new Date(Math.min(...ms) - ANTES_H * HORA).toISOString() : "";
}

/* «27/10/2026 13:17 · MICA S.A. (TRELEW) · 276,6 l» */
export function resumenTicket(t) {
  const lugar = [t?.establecimiento, t?.localidad ? `(${t.localidad})` : ""].filter(Boolean).join(" ");
  const lts = Number(t?.litros);
  return [
    t?.fecha_hora ? diaHoraAr(t.fecha_hora) : t?.recibida_en ? `llegó ${diaHoraAr(t.recibida_en)}` : "",
    lugar,
    t?.litros != null && Number.isFinite(lts) ? `${lts.toLocaleString("es-AR", { maximumFractionDigits: 1 })} l` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/* Lo que hay que mirar de ese ticket contra la orden: que no parezca un
 * ticket, o que los litros no coincidan (más del 5 %; con tanque completo no
 * hay con qué comparar). [] si está todo bien. */
export function alertasTicket(orden, t) {
  const alertas = [];
  if (t?.es_ticket === false || t?.estado === "no_es_ticket") alertas.push("la foto no parece un ticket");
  else if (t?.estado === "ilegible") alertas.push("no se pudo leer la foto");
  const pedidos = orden?.tanque_completo === true ? null : litrosValidos(orden?.litros);
  const leidos = t?.litros == null ? NaN : Number(t.litros);
  if (pedidos && Number.isFinite(leidos) && Math.abs(leidos - pedidos) > pedidos * TOLERANCIA_LITROS) {
    alertas.push(`cargó ${leidos.toLocaleString("es-AR", { maximumFractionDigits: 1 })} l, la orden dice ${pedidos} l`);
  }
  return alertas;
}

/* ── Quién pide más órdenes (Nico, 29/10, ticket 561) ────────────────────────
 *
 * «Me gustaría filtrar la info y saber quién es el que más pide órdenes de
 * gas oil». El historial se mira por fechas (por defecto, los últimos
 * `DIAS_POR_DEFECTO` días) y arriba va el ranking de esas fechas: órdenes y
 * litros de cada uno. «Quién pide» puede ser quien GENERÓ la orden en el hub
 * (`usuario`, su mail, con `usuario_nombre`) o el CHOFER al que se le dio
 * (`dni`, con `chofer`); arranca por quien la generó y se cambia con un
 * selector. Tocar un nombre deja en el historial sólo las suyas. */

export const DIAS_POR_DEFECTO = 30;
export const POR = [
  { id: "quien", nombre: "quien la generó" },
  { id: "chofer", nombre: "chofer" },
];

const DIA = 24 * HORA;
const esDia = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v ?? ""));

/* Desde hace 30 días hasta hoy, días argentinos («YYYY-MM-DD»). */
export function rangoPorDefecto(ahora = new Date(), dias = DIAS_POR_DEFECTO) {
  const ms = ahora instanceof Date ? ahora.getTime() : ahora;
  return { desde: hoyAr(new Date(ms - dias * DIA)), hasta: hoyAr(new Date(ms)) };
}

/* Los días del rango como instantes para la consulta: desde la medianoche
 * argentina del primero (inclusive) hasta la del día siguiente al último
 * (exclusive). Un día vacío o mal escrito no filtra. */
export function limitesDelRango({ desde, hasta } = {}) {
  const sig = esDia(hasta) ? new Date(Date.parse(`${hasta}T12:00:00Z`) + DIA).toISOString().slice(0, 10) : "";
  return {
    desde: esDia(desde) ? `${desde}T00:00:00-03:00` : "",
    hasta: sig ? `${sig}T00:00:00-03:00` : "",
  };
}

const norma = (v) => renglon(v).toLowerCase();

/* La clave y el nombre de quien pidió esa orden, según `por`. */
export function quienPidio(f, por = "quien") {
  if (por === "chofer") {
    const dni = dniNorm(f?.dni);
    const nombre = renglon(f?.chofer);
    return { clave: dni ? `dni:${dni}` : `nombre:${norma(nombre)}`, nombre: nombre || dniLindo(dni) || "Sin chofer" };
  }
  const mail = norma(f?.usuario);
  const nombre = renglon(f?.usuario_nombre) || renglon(f?.usuario);
  return { clave: mail ? `mail:${mail}` : `nombre:${norma(nombre)}`, nombre: nombre || "Sin nombre" };
}

/* El ranking: [{ clave, nombre, ordenes, litros, tanques }], el que más
 * órdenes tiene primero (a igual cantidad, el de más litros). `litros` son
 * los de la orden —los autorizados si se cambiaron— y las de tanque completo
 * se cuentan aparte, porque no tienen litros que sumar. */
export function rankingPedidos(filas, por = "quien") {
  const grupos = new Map();
  for (const f of filas || []) {
    const { clave, nombre } = quienPidio(f, por);
    const g = grupos.get(clave) || { clave, nombre, ordenes: 0, litros: 0, tanques: 0 };
    g.ordenes += 1;
    const lts = f?.tanque_completo === true ? null : litrosValidos(f?.litros);
    if (lts) g.litros += lts;
    else g.tanques += 1;
    grupos.set(clave, g);
  }
  return [...grupos.values()].sort(
    (a, b) => b.ordenes - a.ordenes || b.litros - a.litros || a.nombre.localeCompare(b.nombre, "es"),
  );
}

/* Las órdenes de uno solo del ranking; sin clave, todas. */
export function filtrarPorQuien(filas, por, clave) {
  if (!clave) return filas || [];
  return (filas || []).filter((f) => quienPidio(f, por).clave === clave);
}

/* «4.200 l + 3 tanque completo», «2 tanque completo», «300 l». */
export function litrosDelRanking(g) {
  const partes = [];
  if (g?.litros) partes.push(`${g.litros.toLocaleString("es-AR")} l`);
  if (g?.tanques) partes.push(`${g.tanques} tanque completo`);
  return partes.join(" + ") || "—";
}
